import { useEffect, useState } from 'react';
import { Ruler, ChevronDown } from 'lucide-react';
import { inputClass } from '@/components/ui';
import { cn, todayISO } from '@/lib/utils';
import { sizeSystemsForCategory, sizeOptionsForSystem } from '@/lib/constants';
import type { SizeSystem } from '@/lib/types';
import { PickerSheet } from './PickerSheet';

// Size type selector (category-aware) + value picker, shared by the Add Item
// form and the camera quick-add. Holds only the picker's open state; the value
// (system / size / bespoke) lives in the parent.
export function SizeField({
  category,
  system,
  size,
  isBespoke,
  bespokeDate,
  acquisitionDate,
  allowBespoke = true,
  onSystemChange,
  onSizeChange,
  onBespokeChange,
  onBespokeDateChange,
}: {
  category: string;
  system: SizeSystem;
  size: string;
  isBespoke: boolean;
  bespokeDate: string;
  acquisitionDate?: string;
  allowBespoke?: boolean;
  onSystemChange: (system: SizeSystem) => void;
  onSizeChange: (size: string) => void;
  onBespokeChange: (isBespoke: boolean) => void;
  onBespokeDateChange: (date: string) => void;
}) {
  const [showSize, setShowSize] = useState(false);
  const systems = sizeSystemsForCategory(category);
  const sizeOptions = sizeOptionsForSystem(system);

  // When the category switches between footwear and garments, the current
  // system may no longer be valid — snap to the first valid one and clear.
  useEffect(() => {
    if (isBespoke) return;
    if (!systems.some((s) => s.id === system)) {
      onSystemChange(systems[0].id);
      onSizeChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Size</label>
      <div className="flex gap-2">
        {[...systems, ...(allowBespoke ? [{ id: 'bespoke' as const, label: 'Bespoke' }] : [])].map((t) => {
          const selected = t.id === 'bespoke' ? isBespoke : !isBespoke && system === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                if (t.id === 'bespoke') {
                  onBespokeChange(true);
                  onSizeChange('');
                  if (!bespokeDate) onBespokeDateChange(acquisitionDate || todayISO());
                } else {
                  onBespokeChange(false);
                  onSystemChange(t.id);
                  onSizeChange('');
                }
              }}
              className={cn(
                'flex-1 py-2 rounded-lg text-xs font-medium transition-all',
                selected ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
              )}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {isBespoke ? (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-muted-foreground">Bespoke / made date</label>
          <input
            type="date"
            value={bespokeDate}
            onChange={(e) => onBespokeDateChange(e.target.value)}
            className={inputClass}
          />
          <p className="text-xs text-muted-foreground">
            Marked on your measurements chart so you can see your size then.
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowSize(true)}
          className={cn(inputClass, 'flex items-center justify-between text-left')}
        >
          <span className="flex items-center gap-2">
            <Ruler className="w-4 h-4 text-muted-foreground" />
            <span className={size ? 'text-foreground' : 'text-muted-foreground'}>{size || 'Select size'}</span>
          </span>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </button>
      )}

      <PickerSheet isOpen={showSize} onClose={() => setShowSize(false)} title="Select Size">
        <div className="grid grid-cols-4 gap-2 p-2">
          {sizeOptions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                onSizeChange(s);
                setShowSize(false);
              }}
              className={cn(
                'py-3 rounded-xl text-sm font-medium transition-all',
                size === s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </PickerSheet>
    </div>
  );
}
